/**
 * Number display for Inspector fields. Measured values always show a decimal
 * ("0.0", like Unreal's details panel), counts stay integers, and float noise
 * from arithmetic (0.30000000000000004) never reaches the screen.
 */

export interface NumberFormat {
  /** The field's step: sets the minimum precision shown. */
  readonly step?: number;
  /** A unit means a measured quantity, which always shows a decimal. */
  readonly unit?: string;
}

/** Enough precision to be exact for authored values, without float noise. */
const MAX_DECIMALS = 6;

export function formatNumber(value: number, { step, unit }: NumberFormat): string {
  const clean = Number(value.toFixed(MAX_DECIMALS));
  const isCount = step !== undefined && Number.isInteger(step) && unit === undefined;
  const minimum = isCount ? 0 : Math.max(1, decimalsOf(step ?? 0.1));

  return clean.toFixed(Math.max(minimum, decimalsOf(clean)));
}

function decimalsOf(value: number): number {
  const [, fraction = ""] = String(value).split(".");
  return fraction.length;
}

/** The width a log timestamp reserves: room for `[9999.999]`. */
const LOG_TIME_WIDTH = "[9999.999]".length;

/**
 * A log line's timestamp, `   [0.112]`: seconds since boot to the millisecond,
 * right-aligned in a fixed width so lines stay aligned as the seconds gain
 * digits. The padding is spaces, so the element must keep whitespace
 * (`white-space: pre`).
 */
export function formatLogTime(seconds: number): string {
  return `[${seconds.toFixed(3)}]`.padStart(LOG_TIME_WIDTH);
}
